import { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { NAV, PHONE, PHONE_TEL, scrollToId } from "@/data/site";

export const Logo = ({ light = false }) => (
  <span className="flex items-center gap-2.5">
    <img src="/img/tree.png" alt="HF Landscape Solutions tree logo" className={`h-10 w-auto ${light ? "brightness-0 invert" : ""}`} />
    <span className="flex flex-col leading-none">
      <span className={`font-display text-2xl font-black ${light ? "text-white" : "text-[var(--ink)]"}`}>HF</span>
      <span className={`text-[11px] font-bold tracking-wide ${light ? "text-white/80" : "text-[var(--ink)]"}`}>Landscape Solutions</span>
    </span>
  </span>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${scrolled || open ? "bg-white/85 py-3 shadow-[0_1px_0_rgba(27,107,31,0.12)] backdrop-blur-xl" : "bg-transparent py-5"}`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <button data-testid="nav-logo" onClick={() => go("top")} aria-label="Back to top"><Logo /></button>
        <nav className="hidden items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-link-${n.id}`} onClick={() => go(n.id)} className="group relative text-sm font-semibold text-[var(--ink)]">
              {n.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-full origin-left scale-x-0 bg-[var(--leaf)] transition-transform duration-300 group-hover:scale-x-100" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a href={PHONE_TEL} data-testid="nav-call-btn" className="hidden items-center gap-2 rounded-full border border-[var(--leaf)]/25 px-4 py-2.5 text-sm font-bold text-[var(--leaf)] transition-colors hover:bg-[var(--sprout)] sm:flex">
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
          <button data-testid="nav-quote-btn" onClick={() => go("contact")} className="rounded-full bg-[var(--leaf)] px-5 py-2.5 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--leaf-deep)]">
            Free Quote
          </button>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen((o) => !o)} className="grid h-10 w-10 place-items-center rounded-full border border-black/10 lg:hidden" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="mx-5 mt-3 rounded-2xl border border-black/5 bg-white p-3 lg:hidden">
          {NAV.concat({ id: "contact", label: "Contact" }).map((n) => (
            <button key={n.id} data-testid={`mobile-link-${n.id}`} onClick={() => go(n.id)} className="block w-full rounded-xl px-4 py-3 text-left font-display text-xl font-bold hover:bg-[var(--mist)]">
              {n.label}
            </button>
          ))}
          <a href={PHONE_TEL} data-testid="mobile-menu-call" className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-[var(--sprout)] py-3 font-bold text-[var(--leaf)]">
            <Phone className="h-4 w-4" /> Call {PHONE}
          </a>
        </div>
      )}
    </header>
  );
};
